import { IDeviceRow, IDeviceSearchData, OptionalDeviceApplicationsType } from '~/models'
import { camalize } from './commonServices'

type DeviceApplicationOptionType = {
  selected: boolean
  text: string
  value: number
}
export type FormattededDeviceApplicationType = {
  disabled: boolean
  name: string
  options: DeviceApplicationOptionType[]
  selectedValue?: number
}

/**
 * Transforms an array of `OptionalDeviceApplicationsType` into an array of `TransformedDeviceApplicationType`.
 *
 * @param inputArray - The input array of device application options.
 * @returns An array of transformed device application groups.
 *
 * @remarks
 * This function groups the input array by `group.name` and transforms each group into a new object of type
 * `TransformedDeviceApplicationType`. Each group object contains an array of options, where each option is an object
 * with `selected`, `text`, and `value` properties.
 *
 * The function uses a helper function `findGroupByName` to check if a group with the same name already exists in the
 * transformed array. If a group with the same name is found, the option is added to the existing group's options array.
 * If a group with the same name is not found, a new group object is created and added to the transformed array.
 */
export function FormatDeviceApplication(
  inputArray: OptionalDeviceApplicationsType[]
): FormattededDeviceApplicationType[] {
  const transformedArray = [] as FormattededDeviceApplicationType[]

  // Helper function to find an existing group by name in the transformedArray
  const findGroupByName = (groupName: any) =>
    transformedArray.find(group => group.name === groupName)

  // Transform the input array
  inputArray.forEach(item => {
    const { group, selected, text, value } = item
    const groupName = group.name

    // Check if the group already exists in the transformed array
    let existingGroup = findGroupByName(groupName)

    if (!existingGroup) {
      // Create a new group entry if it doesn't exist
      existingGroup = {
        disabled: group.disabled,
        name: groupName,
        options: []
      }
      transformedArray.push(existingGroup)
    }

    // Add the option to the group
    existingGroup.options.push({
      selected,
      text,
      value: parseInt(value)
    })
  })

  return transformedArray
}

export function convertDevice(devices: IDeviceSearchData[]): IDeviceRow[] {
  return devices.map(device => {
    const convertedDevice: IDeviceRow = {
      otapId: device.otapId,
      deviceStateId: device.deviceStateId,
      otapState: device.otapState,
      deviceOtapStateId: device.deviceOtapStateId,
      serialNumber: device.assignedAssets.map(item => item.serialNumber),
      individualManifestSizeDisplayString: device.individualManifestSizeDisplayString,
      deployingIndividualManifestId: device.deployingIndividualManifestId,
      hasDuplicateValues: device.hasDuplicateValues,
      isModified: device.isModified,
      hasEmptyValues: device.hasEmptyValues,
      groupName: device.groupName,
      groupId: device.groupId,
      assignedAssetsLength: device.assignedAssets?.length ?? 0
    }

    device.deviceSummaryValues.forEach(summaryValue => {
      convertedDevice[camalize(summaryValue.columnName)] = summaryValue.value
    })

    return convertedDevice
  })
}
